import React, { useState } from 'react';
import ReactModal from 'react-modal';
import { generateInvoiceCsv } from '../../api/admin';
import './RosterExportModal.css';

const InvoiceExportModal = ({ programIds, onClose, onSuccess }) => {
  const [step, setStep] = useState('confirm'); // confirm, loading, success, error
  const [statusUpdate, setStatusUpdate] = useState('none'); // none, invoiced, paid
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const doExport = async () => {
    setStep('loading');
    setErrorMessage('');
    const statusArg = statusUpdate === 'none' ? null : statusUpdate;
    try {
      const response = await generateInvoiceCsv(programIds, statusArg);
      if (response.status === 'success') {
        setResult(response.data);
        setStep('success');
        const included = response.data.includedProgramIds || programIds;
        if (onSuccess && statusArg) onSuccess(included, statusArg);
      } else {
        setErrorMessage(response.message || 'Failed to generate invoice CSV');
        setStep('error');
      }
    } catch (error) {
      setErrorMessage(error.message || 'An unexpected error occurred');
      setStep('error');
    }
  };

  const downloadCsvFile = (filename, base64Content) => {
    const binaryString = atob(base64Content);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownload = () => {
    if (result.fileContent) {
      downloadCsvFile(result.filename, result.fileContent);
    } else if (result.downloadUrl) {
      window.open(result.downloadUrl, '_blank');
    }
  };

  const renderConfirm = () => (
    <div className="roster-export-content">
      <h3>Generate Invoice CSV</h3>
      <p>
        Generate a QuickBooks invoice CSV for {programIds.length} program
        {programIds.length > 1 ? 's' : ''}.
      </p>
      <div className="invoice-status-options">
        <p className="status-options-label">After download, set program status to:</p>
        <label>
          <input
            type="radio"
            name="statusUpdate"
            value="none"
            checked={statusUpdate === 'none'}
            onChange={() => setStatusUpdate('none')}
          />
          <span>Leave unchanged</span>
        </label>
        <label>
          <input
            type="radio"
            name="statusUpdate"
            value="invoiced"
            checked={statusUpdate === 'invoiced'}
            onChange={() => setStatusUpdate('invoiced')}
          />
          <span>Invoiced</span>
        </label>
        <label>
          <input
            type="radio"
            name="statusUpdate"
            value="paid"
            checked={statusUpdate === 'paid'}
            onChange={() => setStatusUpdate('paid')}
          />
          <span>Paid</span>
        </label>
      </div>
      <div className="modal-actions">
        <button className="primary-button" onClick={doExport}>Generate</button>
        <button className="secondary-button" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );

  const renderLoading = () => (
    <div className="roster-export-content">
      <div className="loading-spinner"></div>
      <h3>Generating Invoice CSV...</h3>
      <p>
        Please wait while we generate the invoice for {programIds.length} program
        {programIds.length > 1 ? 's' : ''}.
      </p>
    </div>
  );

  const renderSuccess = () => {
    const derived = result.derived || [];
    return (
      <div className="roster-export-content">
        <div className="success-icon">&#10003;</div>
        <h3>Invoice CSV Generated Successfully</h3>
        <div className="result-details">
          <p><strong>File:</strong> {result.filename}</p>
          <p>
            <strong>Invoiced:</strong> {result.invoicedCount} program
            {result.invoicedCount === 1 ? '' : 's'}
          </p>
          {statusUpdate !== 'none' && (
            <p><strong>Status set to:</strong> {statusUpdate}</p>
          )}
          {derived.length > 0 && (
            <div className="invoice-skipped">
              <p><strong>Verify {derived.length} in QuickBooks</strong> (derived name — no invoice mapping):</p>
              <ul>
                {derived.map((d) => (
                  <li key={d.programId + d.reason}>{d.reason}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="modal-actions">
          <button className="primary-button" onClick={handleDownload}>Download CSV</button>
          <button className="secondary-button" onClick={onClose}>Close</button>
        </div>
      </div>
    );
  };

  const renderError = () => (
    <div className="roster-export-content">
      <div className="error-icon">&#10007;</div>
      <h3>Invoice Generation Failed</h3>
      <p className="error-message">{errorMessage}</p>
      <div className="modal-actions">
        <button className="primary-button" onClick={doExport}>Retry</button>
        <button className="secondary-button" onClick={onClose}>Close</button>
      </div>
    </div>
  );

  return (
    <ReactModal
      isOpen={true}
      onRequestClose={step !== 'loading' ? onClose : undefined}
      shouldCloseOnOverlayClick={step !== 'loading'}
      contentLabel="Invoice Export"
      ariaHideApp={false}
      className="roster-export-modal-content"
      overlayClassName="modal-overlay"
    >
      <div className="roster-export-modal">
        {step !== 'loading' && (
          <button className="close-button" onClick={onClose}>x</button>
        )}
        {step === 'confirm' && renderConfirm()}
        {step === 'loading' && renderLoading()}
        {step === 'success' && renderSuccess()}
        {step === 'error' && renderError()}
      </div>
    </ReactModal>
  );
};

export default InvoiceExportModal;
